import { Button, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { ApiError } from '../../lib/apiFetch.js';
import type { QcChecklistSummary } from './contractsMirror.js';
import { QcRowActions } from './QcRowActions.js';
import { useCreateQcChecklistMutation, useQcTemplatesQuery } from './useQc.js';

/**
 * "What is not finished."
 *
 * The driver for the whole feature is jobs being closed out incomplete, so this screen answers that
 * question first: unfinished work at the top, approved work last, overdue surfaced by date.
 *
 * Two different numbers are shown per row on purpose. UNANSWERED is work not done. OUTSTANDING is
 * work that failed or needs review and has not been waived — that one is what will block sign-off.
 * Collapsing them into a single "progress" figure would hide a failing item behind a full bar.
 */

const CONTROL =
  'h-11 w-full min-w-0 rounded-md border border-border bg-background px-3 text-foreground text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background';

export interface QcListViewProps {
  checklists: QcChecklistSummary[];
  mineOnly: boolean;
  onToggleMine: (next: boolean) => void;
  showArchived: boolean;
  onToggleArchived: (next: boolean) => void;
}

export function QcListView({
  checklists,
  mineOnly,
  onToggleMine,
  showArchived,
  onToggleArchived,
}: QcListViewProps) {
  const { t } = useTranslation();
  const canAccess = useAccess();
  const canStart =
    canAccess({ permission: 'qc.complete' }) || canAccess({ permission: 'qc.approve' });
  // Mirrors the api: archiving and deleting are both qc.approve, not qc.complete. Somebody filling
  // a checklist in should not be able to make it disappear.
  const canManage = canAccess({ permission: 'qc.approve' });

  const templates = useQcTemplatesQuery(canStart);
  const create = useCreateQcChecklistMutation();

  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');

  const errorMessage =
    create.error instanceof ApiError
      ? t(`qc:errors.${create.error.message}`, { defaultValue: t('qc:errors.generic') })
      : null;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-full bg-background px-4 py-6 sm:px-6 sm:py-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-semibold text-2xl text-foreground sm:text-3xl">
            {t('qc:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('qc:subtitle')}</p>
        </div>
        {canStart ? (
          <Button type="button" onClick={() => setOpen((v) => !v)}>
            {t('qc:actions.start')}
          </Button>
        ) : null}
      </header>

      {open && canStart ? (
        <section
          aria-label={t('qc:create.regionLabel')}
          className="mb-4 rounded-md border border-border bg-card p-4"
        >
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const id = Number(templateId);
              if (!id) return;
              create.mutate(
                { templateId: id, name: name.trim() || undefined, dueDate: dueDate || null },
                {
                  onSuccess: () => {
                    setOpen(false);
                    setTemplateId('');
                    setName('');
                    setDueDate('');
                  },
                },
              );
            }}
          >
            <div className="w-full sm:w-72">
              <label htmlFor="qc-template" className="font-medium text-foreground text-xs">
                {t('qc:create.template')}
              </label>
              <select
                id="qc-template"
                className={CONTROL}
                value={templateId}
                required
                onChange={(e) => setTemplateId(e.target.value)}
              >
                <option value="">{t('qc:create.templatePlaceholder')}</option>
                {(templates.data?.templates ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {t('qc:create.templateOption', {
                      name: template.name,
                      count: template.itemCount,
                    })}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full min-w-0 sm:flex-1">
              <label htmlFor="qc-name" className="font-medium text-foreground text-xs">
                {t('qc:create.name')}
              </label>
              <input
                id="qc-name"
                type="text"
                className={CONTROL}
                value={name}
                maxLength={160}
                placeholder={t('qc:create.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="w-full sm:w-48">
              <label htmlFor="qc-due" className="font-medium text-foreground text-xs">
                {t('qc:create.dueDate')}
              </label>
              <input
                id="qc-due"
                type="date"
                className={CONTROL}
                value={dueDate}
                min={today}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            <Button type="submit" disabled={create.isPending || !templateId}>
              {t('qc:create.submit')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              {t('qc:create.cancel')}
            </Button>

            {errorMessage ? (
              <p role="alert" className="w-full text-destructive text-sm">
                {errorMessage}
              </p>
            ) : null}
          </form>
        </section>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={mineOnly ? 'primary' : 'secondary'}
          aria-pressed={mineOnly}
          onClick={() => onToggleMine(!mineOnly)}
        >
          {t('qc:filters.mine')}
        </Button>
        {/* Offered to everyone who can read QC, not only to whoever can archive: seeing that a
            checklist was retired rather than lost is the answer to "where did it go". */}
        <Button
          type="button"
          size="sm"
          variant={showArchived ? 'primary' : 'secondary'}
          aria-pressed={showArchived}
          onClick={() => onToggleArchived(!showArchived)}
        >
          {t('qc:filters.showArchived')}
        </Button>
      </div>

      {checklists.length === 0 ? (
        <section className="rounded-md border border-border bg-card p-8 text-center">
          <h2 className="font-display font-semibold text-foreground text-xl">{t('qc:empty')}</h2>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground text-sm">{t('qc:emptyBody')}</p>
          {canStart ? (
            <Button type="button" className="mt-4" onClick={() => setOpen(true)}>
              {t('qc:actions.start')}
            </Button>
          ) : null}
        </section>
      ) : (
        <ul className="flex flex-col gap-2">
          {checklists.map((checklist) => {
            const overdue =
              checklist.dueDate !== null &&
              checklist.dueDate < today &&
              checklist.status !== 'approved';
            const archived = checklist.archivedAt !== null;
            return (
              // The actions are SIBLINGS of the link, never inside it. A button nested in an anchor
              // is invalid, and it navigates as well as acting — one tap on Delete would open the
              // checklist it just destroyed.
              <li
                key={checklist.id}
                className={`flex flex-col rounded-md border border-border bg-card sm:flex-row sm:items-center ${
                  // Retired rows read as background. Not colour alone — they also carry the word.
                  archived ? 'opacity-60' : ''
                }`}
              >
                <Link
                  to="/qc/$id"
                  params={{ id: String(checklist.id) }}
                  className="flex min-h-11 min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-foreground text-sm">
                      {checklist.name}
                    </span>
                    <span className="block truncate text-muted-foreground text-xs">
                      {checklist.jobName
                        ? t('qc:row.onJob', {
                            job: checklist.jobName,
                            template: checklist.templateName,
                          })
                        : checklist.templateName}
                    </span>
                  </span>

                  {checklist.outstandingCount > 0 ? (
                    <span className="whitespace-nowrap rounded bg-destructive/10 px-1.5 py-0.5 font-medium text-destructive text-xs">
                      {t('qc:row.outstanding', { count: checklist.outstandingCount })}
                    </span>
                  ) : null}

                  {overdue ? (
                    <span className="whitespace-nowrap rounded bg-warning/12 px-1.5 py-0.5 font-medium text-warning text-xs">
                      {t('qc:row.overdue')}
                    </span>
                  ) : null}

                  <span className="whitespace-nowrap font-mono text-muted-foreground text-xs">
                    {checklist.answeredCount}/{checklist.itemCount}
                  </span>
                  <span className="whitespace-nowrap rounded bg-secondary px-2 py-0.5 text-secondary-foreground text-xs">
                    {t(`qc:status.${checklist.status}`)}
                  </span>
                  {archived ? (
                    <span className="whitespace-nowrap rounded bg-secondary px-2 py-0.5 text-secondary-foreground text-xs">
                      {t('qc:row.archived')}
                    </span>
                  ) : null}
                </Link>
                {canManage ? <QcRowActions checklist={checklist} /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
